/*
 * Drawing kit for the PDF reports: icons, cards, chips, tables and charts.
 * Everything takes an existing jsPDF instance so the report file can stay
 * about layout rather than geometry.
 */

export const PALETTE = {
  ink: [15, 23, 42],
  body: [71, 85, 105],
  muted: [136, 145, 166],
  hair: [228, 231, 240],
  white: [255, 255, 255],
  // One indigo ramp carries every magnitude in the report.
  ramp: {
    wash: [239, 241, 254],
    track: [219, 223, 251],
    mid: [151, 156, 240],
    fill: [79, 70, 229],
    deep: [55, 48, 163]
  },
  // Soft card tints — decoration only, never an encoding.
  tint: {
    indigo: [238, 241, 254],
    mint: [232, 248, 240],
    peach: [255, 244, 230],
    lilac: [245, 238, 253],
    slate: [246, 247, 251]
  },
  // Status palette, fixed. Marks only — labels stay in ink.
  status: {
    good: [12, 163, 12],
    warning: [250, 178, 25],
    critical: [208, 59, 59],
    idle: [148, 157, 178]
  }
};

const TAU = Math.PI * 2;

export const createKit = (doc) => {
  const setInk = (c) => doc.setTextColor(c[0], c[1], c[2]);
  const setFill = (c) => doc.setFillColor(c[0], c[1], c[2]);
  const setStroke = (c) => doc.setDrawColor(c[0], c[1], c[2]);

  /** Draw with a temporary opacity, always restoring it afterwards. */
  const faded = (opacity, draw) => {
    doc.setGState(new doc.GState({ opacity }));
    draw();
    doc.setGState(new doc.GState({ opacity: 1 }));
  };

  const card = (x, y, w, h, opts = {}) => {
    const { fill = PALETTE.tint.slate, radius = 3.5, accent, border } = opts;
    setFill(fill);
    doc.roundedRect(x, y, w, h, radius, radius, 'F');
    if (border) {
      setStroke(border);
      doc.setLineWidth(0.3);
      doc.roundedRect(x, y, w, h, radius, radius, 'S');
    }
    if (accent) {
      // A rounded bar tucked against the left edge.
      setFill(accent);
      doc.roundedRect(x, y + 2.5, 1.4, h - 5, 0.7, 0.7, 'F');
    }
  };

  // ── icons ─────────────────────────────────────────────────────────────────
  // Each draws inside a size×size box with its top-left at (x, y).

  const strokeSetup = (color, weight = 0.42) => {
    setStroke(color);
    doc.setLineWidth(weight);
    doc.setLineCap('round');
    doc.setLineJoin('round');
  };

  const icons = {
    target: (x, y, s, color) => {
      strokeSetup(color);
      const c = s / 2;
      doc.circle(x + c, y + c, s * 0.44, 'S');
      doc.circle(x + c, y + c, s * 0.22, 'S');
      setFill(color);
      doc.circle(x + c, y + c, s * 0.07, 'F');
    },
    clock: (x, y, s, color) => {
      strokeSetup(color);
      const c = s / 2;
      doc.circle(x + c, y + c, s * 0.44, 'S');
      doc.line(x + c, y + c, x + c, y + c - s * 0.24);
      doc.line(x + c, y + c, x + c + s * 0.18, y + c + s * 0.1);
    },
    calendar: (x, y, s, color) => {
      strokeSetup(color, 0.38);
      doc.roundedRect(x + s * 0.08, y + s * 0.16, s * 0.84, s * 0.76, 0.6, 0.6, 'S');
      doc.line(x + s * 0.08, y + s * 0.4, x + s * 0.92, y + s * 0.4);
      doc.line(x + s * 0.3, y + s * 0.06, x + s * 0.3, y + s * 0.24);
      doc.line(x + s * 0.7, y + s * 0.06, x + s * 0.7, y + s * 0.24);
    },
    user: (x, y, s, color) => {
      strokeSetup(color);
      doc.circle(x + s / 2, y + s * 0.34, s * 0.19, 'S');
      doc.lines([[s * 0.16, -s * 0.26], [s * 0.36, 0], [s * 0.16, s * 0.26]],
        x + s * 0.16, y + s * 0.92, [1, 1], 'S');
    },
    flag: (x, y, s, color) => {
      strokeSetup(color);
      doc.line(x + s * 0.22, y + s * 0.08, x + s * 0.22, y + s * 0.94);
      setFill(color);
      doc.triangle(x + s * 0.22, y + s * 0.14, x + s * 0.84, y + s * 0.32,
        x + s * 0.22, y + s * 0.5, 'F');
    },
    check: (x, y, s, color) => {
      strokeSetup(color, 0.55);
      doc.line(x + s * 0.2, y + s * 0.52, x + s * 0.42, y + s * 0.74);
      doc.line(x + s * 0.42, y + s * 0.74, x + s * 0.82, y + s * 0.26);
    },
    alert: (x, y, s, color) => {
      strokeSetup(color, 0.4);
      doc.lines([[s * 0.42, s * 0.78], [-s * 0.84, 0], [s * 0.42, -s * 0.78]],
        x + s * 0.08, y + s * 0.12, [1, 1], 'S');
      doc.line(x + s / 2, y + s * 0.38, x + s / 2, y + s * 0.62);
      setFill(color);
      doc.circle(x + s / 2, y + s * 0.75, s * 0.05, 'F');
    },
    trend: (x, y, s, color) => {
      strokeSetup(color);
      doc.lines([[s * 0.24, -s * 0.3], [s * 0.22, s * 0.16], [s * 0.36, -s * 0.42]],
        x + s * 0.1, y + s * 0.74, [1, 1], 'S');
      doc.line(x + s * 0.62, y + s * 0.18, x + s * 0.92, y + s * 0.18);
      doc.line(x + s * 0.92, y + s * 0.18, x + s * 0.92, y + s * 0.46);
    },
    star: (x, y, s, color) => {
      const c = s / 2;
      const pts = [];
      for (let i = 0; i < 10; i += 1) {
        const r = i % 2 === 0 ? s * 0.46 : s * 0.2;
        const a = -Math.PI / 2 + (i * Math.PI) / 5;
        pts.push([x + c + r * Math.cos(a), y + c + r * Math.sin(a)]);
      }
      setFill(color);
      for (let i = 1; i < pts.length - 1; i += 1) {
        doc.triangle(pts[0][0], pts[0][1], pts[i][0], pts[i][1],
          pts[i + 1][0], pts[i + 1][1], 'F');
      }
    },
    bulb: (x, y, s, color) => {
      strokeSetup(color, 0.38);
      doc.circle(x + s / 2, y + s * 0.4, s * 0.28, 'S');
      doc.line(x + s * 0.36, y + s * 0.74, x + s * 0.64, y + s * 0.74);
      doc.line(x + s * 0.4, y + s * 0.88, x + s * 0.6, y + s * 0.88);
    },
    compass: (x, y, s, color) => {
      strokeSetup(color, 0.38);
      doc.circle(x + s / 2, y + s / 2, s * 0.44, 'S');
      setFill(color);
      doc.triangle(x + s * 0.34, y + s * 0.66, x + s * 0.68, y + s * 0.32,
        x + s * 0.5, y + s * 0.5, 'F');
    },
    bars: (x, y, s, color) => {
      setFill(color);
      doc.roundedRect(x + s * 0.12, y + s * 0.54, s * 0.16, s * 0.38, 0.4, 0.4, 'F');
      doc.roundedRect(x + s * 0.42, y + s * 0.3, s * 0.16, s * 0.62, 0.4, 0.4, 'F');
      doc.roundedRect(x + s * 0.72, y + s * 0.12, s * 0.16, s * 0.8, 0.4, 0.4, 'F');
    },
    quote: (x, y, s, color) => {
      setFill(color);
      doc.circle(x + s * 0.28, y + s * 0.35, s * 0.16, 'F');
      doc.circle(x + s * 0.72, y + s * 0.35, s * 0.16, 'F');
      doc.triangle(x + s * 0.14, y + s * 0.38, x + s * 0.42, y + s * 0.38,
        x + s * 0.2, y + s * 0.78, 'F');
      doc.triangle(x + s * 0.58, y + s * 0.38, x + s * 0.86, y + s * 0.38,
        x + s * 0.64, y + s * 0.78, 'F');
    },
    milestone: (x, y, s, color) => {
      strokeSetup(color, 0.4);
      doc.line(x + s * 0.5, y + s * 0.1, x + s * 0.5, y + s * 0.9);
      setFill(color);
      doc.circle(x + s * 0.5, y + s * 0.5, s * 0.2, 'F');
    }
  };

  /** An icon inside a soft circular badge. */
  const iconBadge = (name, x, y, opts = {}) => {
    const { radius = 4, tint = PALETTE.ramp.wash, color = PALETTE.ramp.fill, size = 4.4 } = opts;
    setFill(tint);
    doc.circle(x + radius, y + radius, radius, 'F');
    (icons[name] || icons.target)(x + radius - size / 2, y + radius - size / 2, size, color);
  };

  // ── chips ─────────────────────────────────────────────────────────────────

  const chip = (label, x, y, opts = {}) => {
    const {
      icon,
      mark,
      tint = PALETTE.tint.slate,
      color = PALETTE.ramp.fill,
      height = 6.4,
      size = 7.6
    } = opts;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(size);
    const lead = icon || mark ? 6.4 : 3.4;
    const width = doc.getTextWidth(label) + lead + 4;
    setFill(tint);
    doc.roundedRect(x, y, width, height, height / 2, height / 2, 'F');
    if (mark) {
      setFill(mark);
      doc.circle(x + 3.1, y + height / 2, 1.1, 'F');
    } else if (icon) {
      (icons[icon] || icons.target)(x + 2, y + height / 2 - 1.7, 3.4, color);
    }
    setInk(PALETTE.ink);
    doc.text(label, x + lead, y + height / 2 + 1.05);
    return width;
  };

  // ── charts ────────────────────────────────────────────────────────────────

  const arc = (cx, cy, r, from, to, color, weight) => {
    setStroke(color);
    doc.setLineWidth(weight);
    doc.setLineCap('round');
    const steps = Math.max(2, Math.ceil((Math.abs(to - from) / TAU) * 90));
    for (let i = 0; i < steps; i += 1) {
      const a1 = from + ((to - from) * i) / steps;
      const a2 = from + ((to - from) * (i + 1)) / steps;
      doc.line(cx + r * Math.cos(a1), cy + r * Math.sin(a1),
        cx + r * Math.cos(a2), cy + r * Math.sin(a2));
    }
  };

  /**
   * Radial meter: one ratio against its limit. Track is a light step of the
   * same hue as the fill, so state reads around the whole ring.
   */
  const ring = (cx, cy, radius, pct, opts = {}) => {
    const {
      weight = 5.5,
      track = PALETTE.ramp.track,
      fill = PALETTE.ramp.fill,
      value,
      caption,
      valueSize = 17,
      captionSize = 6.6
    } = opts;
    setStroke(track);
    doc.setLineWidth(weight);
    doc.setLineCap('butt');
    const steps = 90;
    for (let i = 0; i < steps; i += 1) {
      const a1 = (i / steps) * TAU;
      const a2 = ((i + 1) / steps) * TAU;
      doc.line(cx + radius * Math.cos(a1), cy + radius * Math.sin(a1),
        cx + radius * Math.cos(a2), cy + radius * Math.sin(a2));
    }
    const fraction = Math.max(0, Math.min(100, pct)) / 100;
    if (fraction > 0.002) {
      const start = -Math.PI / 2;
      arc(cx, cy, radius, start, start + TAU * fraction, fill, weight);
    }
    if (value !== undefined) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(valueSize);
      setInk(PALETTE.ink);
      doc.text(String(value), cx, cy + (caption ? 0.8 : 2), { align: 'center' });
    }
    if (caption) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(captionSize);
      setInk(PALETTE.muted);
      doc.text(caption, cx, cy + 5, { align: 'center' });
    }
  };

  /** Linear meter: same ramp, rounded ends. */
  const meter = (x, y, w, pct, opts = {}) => {
    const { height = 3.6, track = PALETTE.ramp.track, fill = PALETTE.ramp.fill } = opts;
    setFill(track);
    doc.roundedRect(x, y, w, height, height / 2, height / 2, 'F');
    const filled = (Math.max(0, Math.min(100, pct)) / 100) * w;
    if (filled > height / 2) {
      setFill(fill);
      doc.roundedRect(x, y, filled, height, height / 2, height / 2, 'F');
    }
  };

  /**
   * Radar over 5 comparable 0-100 axes. The numbers live in a table beside it,
   * so nothing is gated on reading the shape.
   */
  const radar = (cx, cy, radius, points) => {
    const n = points.length;
    const angleAt = (i) => -Math.PI / 2 + (i * TAU) / n;
    const vertex = (i, value) => {
      const r = radius * Math.max(0.04, Math.min(1, value / 100));
      return [cx + r * Math.cos(angleAt(i)), cy + r * Math.sin(angleAt(i))];
    };

    // Recessive grid: hairline rings at 25/50/75/100.
    setStroke(PALETTE.hair);
    doc.setLineWidth(0.25);
    [0.25, 0.5, 0.75, 1].forEach((step) => {
      const r = radius * step;
      for (let i = 0; i < n; i += 1) {
        const a1 = angleAt(i);
        const a2 = angleAt((i + 1) % n);
        doc.line(cx + r * Math.cos(a1), cy + r * Math.sin(a1),
          cx + r * Math.cos(a2), cy + r * Math.sin(a2));
      }
    });
    for (let i = 0; i < n; i += 1) {
      const a = angleAt(i);
      doc.line(cx, cy, cx + radius * Math.cos(a), cy + radius * Math.sin(a));
    }

    // The series: a wash, then a 2px outline, then vertex dots with a surface ring.
    const verts = points.map((p, i) => vertex(i, p.value));
    setFill(PALETTE.ramp.fill);
    faded(0.16, () => {
      for (let i = 1; i < verts.length - 1; i += 1) {
        doc.triangle(verts[0][0], verts[0][1], verts[i][0], verts[i][1],
          verts[i + 1][0], verts[i + 1][1], 'F');
      }
    });
    setStroke(PALETTE.ramp.fill);
    doc.setLineWidth(0.6);
    doc.setLineJoin('round');
    verts.forEach((v, i) => {
      const next = verts[(i + 1) % verts.length];
      doc.line(v[0], v[1], next[0], next[1]);
    });
    verts.forEach((v) => {
      setFill(PALETTE.white);
      doc.circle(v[0], v[1], 1.5, 'F');
      setFill(PALETTE.ramp.fill);
      doc.circle(v[0], v[1], 0.95, 'F');
    });

    // Axis letters just outside the outer ring.
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    setInk(PALETTE.muted);
    points.forEach((p, i) => {
      const a = angleAt(i);
      doc.text(p.letter, cx + (radius + 4.5) * Math.cos(a),
        cy + (radius + 4.5) * Math.sin(a) + 1.2, { align: 'center' });
    });
  };

  /** Line chart with a soft gradient wash beneath it. */
  const areaChart = (x, y, w, h, series, opts = {}) => {
    const { reference, referenceLabel, endLabel } = opts;
    if (series.length < 2) return;

    const values = series.map((p) => p.value).concat(reference !== null && reference !== undefined ? [reference] : []);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const span = max - min || 1;
    const t0 = series[0].t;
    const tSpan = series[series.length - 1].t - t0 || 1;
    const px = (p) => x + w * ((p.t - t0) / tSpan);
    const py = (v) => y + h - h * ((v - min) / span);

    // Reference line, hairline and solid.
    if (reference !== null && reference !== undefined) {
      setStroke(PALETTE.ramp.track);
      doc.setLineWidth(0.4);
      doc.setLineCap('butt');
      doc.line(x, py(reference), x + w, py(reference));
      if (referenceLabel) {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(6.4);
        setInk(PALETTE.muted);
        doc.text(referenceLabel, x + w + 1.5, py(reference) + 1);
      }
    }

    const pts = series.map((p) => [px(p), py(p.value)]);

    // One flat wash under the line. Banded fills seam visibly in PDF, and a
    // single ~14% area tint is what the mark spec calls for anyway.
    faded(0.14, () => {
      setFill(PALETTE.ramp.fill);
      const base = y + h;
      const first = pts[0];
      const rel = [];
      for (let i = 1; i < pts.length; i += 1) {
        rel.push([pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1]]);
      }
      const lastPt = pts[pts.length - 1];
      rel.push([0, base - lastPt[1]]);
      rel.push([first[0] - lastPt[0], 0]);
      // One closed path, filled once — no overlapping triangles, no seams.
      doc.lines(rel, first[0], first[1], [1, 1], 'F', true);
    });

    setStroke(PALETTE.ramp.fill);
    doc.setLineWidth(0.7);
    doc.setLineCap('round');
    doc.setLineJoin('round');
    for (let i = 0; i < pts.length - 1; i += 1) {
      doc.line(pts[i][0], pts[i][1], pts[i + 1][0], pts[i + 1][1]);
    }

    // Only the endpoint is labelled.
    const last = pts[pts.length - 1];
    setFill(PALETTE.white);
    doc.circle(last[0], last[1], 2, 'F');
    setFill(PALETTE.ramp.fill);
    doc.circle(last[0], last[1], 1.3, 'F');
    if (endLabel) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      setInk(PALETTE.ink);
      doc.text(endLabel, last[0], last[1] - 3.4, { align: 'center' });
    }
  };

  /**
   * Zebra-striped table. Columns: { label, width, align, bold, render }.
   * Pass `ensure(needed, y) -> y` and rows flow onto the next page one at a
   * time, repeating the header, instead of the whole table jumping.
   */
  const table = (x, y, width, columns, rows, opts = {}) => {
    const { rowHeight = 7.6, headerHeight = 6.6, zebra = PALETTE.tint.slate, ensure } = opts;
    let cursor = y;

    const drawHeader = () => {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(6.6);
      setInk(PALETTE.muted);
      let cx = x;
      columns.forEach((column) => {
        doc.text(column.label.toUpperCase(),
          column.align === 'right' ? cx + column.width : cx,
          cursor + 4.4,
          { align: column.align === 'right' ? 'right' : 'left', charSpace: 0.3 });
        cx += column.width;
      });
      cursor += headerHeight;
      setStroke(PALETTE.hair);
      doc.setLineWidth(0.3);
      doc.setLineCap('butt');
      doc.line(x, cursor - 1.6, x + width, cursor - 1.6);
    };

    drawHeader();

    rows.forEach((row, index) => {
      if (ensure) {
        const moved = ensure(rowHeight + 2, cursor);
        if (moved !== cursor) {
          cursor = moved;
          drawHeader();
        }
      }
      if (index % 2 === 1) {
        setFill(zebra);
        doc.roundedRect(x - 2, cursor - 1.2, width + 4, rowHeight, 1.2, 1.2, 'F');
      }
      let colX = x;
      columns.forEach((column) => {
        const value = row[column.key];
        if (column.render) {
          column.render(value, colX, cursor, column.width, row);
        } else {
          doc.setFont('helvetica', column.bold ? 'bold' : 'normal');
          doc.setFontSize(column.size || 8.8);
          setInk(column.bold ? PALETTE.ink : PALETTE.body);
          const text = value === undefined || value === null || value === '' ? '—' : String(value);
          const lines = doc.splitTextToSize(text, column.width - 3);
          doc.text(lines.slice(0, 1),
            column.align === 'right' ? colX + column.width : colX,
            cursor + 4.2,
            { align: column.align === 'right' ? 'right' : 'left' });
        }
        colX += column.width;
      });
      cursor += rowHeight;
    });

    return cursor;
  };

  return {
    setInk, setFill, setStroke, faded,
    card, icons, iconBadge, chip,
    ring, meter, radar, areaChart, arc, table
  };
};

export default createKit;
